import { Component, OnInit, ApplicationRef, effect, signal, inject, ChangeDetectionStrategy } from '@angular/core';
import { IonicModule, Platform, AlertController } from '@ionic/angular';

import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { StatusBar, Style } from '@capacitor/status-bar';
import { filter, first, concat, interval } from 'rxjs';
import { OfflineStorageService } from './services/offline-storage.service';
import { NetworkService } from './services/network.service';
import { SyncService } from './services/sync.service';
import { AppSettingsService } from './services/app-settings.service';
import { MatomoService } from './services/matomo.service';
import { LoggingService } from './services/logging.service';
import { ThemeService } from './services/theme.service';
import { AppStorageService } from './services/app-storage.service';
import { EmbedBridgeService } from './services/embed-bridge.service';
import { MigrationService } from './services/migration.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [IonicModule]
})
export class AppComponent implements OnInit {
  private platform = inject(Platform);
  private offlineStorage = inject(OfflineStorageService);
  private networkService = inject(NetworkService);
  private syncService = inject(SyncService);
  private appSettings = inject(AppSettingsService);
  private matomo = inject(MatomoService);
  private swUpdate = inject(SwUpdate);
  private alertController = inject(AlertController);
  private appRef = inject(ApplicationRef);
  private loggingService = inject(LoggingService);
  private themeService = inject(ThemeService);
  private appStorage = inject(AppStorageService);
  private embedBridge = inject(EmbedBridgeService);
  private migration = inject(MigrationService);

  private platformReady = signal(false);

  constructor() {
    // Before the first await: Notes starts sending hello as soon as the iframe has loaded.
    this.embedBridge.start();

    // Keep the status bar icon/text contrast in sync with the app's theme.
    // Guarded to native platforms (the plugin is a no-op on web) and to
    // platformReady, so the first call happens only once platform.ready()
    // has resolved — same readiness gate as the setOverlaysWebView call in
    // initializeApp(), avoiding a call into the plugin before its native
    // bridge is up.
    effect(() => {
      const isDarkMode = this.themeService.isDarkMode();
      if (!this.platformReady() || !this.platform.is('capacitor')) {
        return;
      }
      StatusBar.setStyle({ style: isDarkMode ? Style.Dark : Style.Light }).catch((error) => {
        this.loggingService.error('Failed to set status bar style:', error);
      });
    });
  }

  async ngOnInit() {
    await this.platform.ready();
    this.platformReady.set(true);
    await this.initializeApp();

    // Setup PWA update detection
    this.setupPwaUpdateDetection();
  }

  private async initializeApp() {
    // Check for ?fix parameter to clear localStorage
    this.checkForFixParameter();

    // Let the WebView draw under the status bar so the app's own
    // safe-area-inset-top CSS (comparison-view toolbar) and Ionic's
    // built-in safe-area handling can reserve the right amount of space.
    // Without this, env(safe-area-inset-top) resolves to 0 on Android and
    // fixed/sticky headers sit underneath the status bar.
    if (this.platform.is('capacitor')) {
      try {
        await StatusBar.setOverlaysWebView({ overlay: true });
      } catch (error) {
        this.loggingService.error('Failed to set status bar overlay mode:', error);
      }
    }

    // Initialize Matomo analytics
    this.matomo.initialize();

    // Initialize offline storage — OfflineStorageService picks the right
    // backend per platform (Capacitor SQLite native, IndexedDB on web).
    // Fire-and-forget: do not block checkForUpdates() scheduling or
    // setupPwaUpdateDetection() below on this resolving — SyncService callers
    // await the same cached promise via OfflineStorageService.waitUntilReady().
    this.offlineStorage
      .initialize()
      .then(async () => {
        if ((await this.syncService.getOfflineResources()).length > 0) {
          await this.syncService.requestPersistentStorage();
        }
      })
      .catch((error) => {
        this.loggingService.error('Failed to initialize offline storage:', error);
      });

    void this.migration.showNoticeIfPending();

    // Check for updates in the background
    if (this.networkService.isOnline()) {
      const updateDelayMs = this.appSettings.getUpdateCheckDelayMs();
      setTimeout(() => {
        this.syncService.checkForUpdates().catch((error) => {
          this.loggingService.error('Failed to check for updates:', error);
        });
      }, updateDelayMs);
    }
  }

  /**
   * Check if URL contains ?fix parameter and clear localStorage if found
   * This allows users to fix broken configurations by accessing: https://example.com?fix
   */
  private checkForFixParameter(): void {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('fix')) {
      try {
        // Only this mode's keys: a standalone ?fix must not wipe the embedded pane's settings, and vice versa.
        this.appStorage.clear();

        // Remove the ?fix parameter from URL without reloading
        const url = new URL(window.location.href);
        url.searchParams.delete('fix');
        window.history.replaceState({}, document.title, url.toString());

        // Show a message to the user
        alert('Configuration has been reset. The app will now reload with default settings.');

        // Reload the page to reinitialize with clean state
        window.location.reload();
      } catch (error) {
        this.loggingService.error('Failed to clear localStorage:', error);
        alert('Failed to reset configuration. Please try again.');
      }
    }
  }

  /**
   * Setup PWA update detection and prompt user when new version is available
   * Uses Angular Service Worker to detect when a new app version has been deployed
   */
  private setupPwaUpdateDetection(): void {
    // Only run in production and when service worker is enabled
    if (!this.swUpdate.isEnabled) {
      return;
    }

    // Check for updates periodically (every 6 hours)
    const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable === true));
    const everyHour$ = interval(6 * 60 * 60 * 1000); // 6 hours in milliseconds
    const everyHourOnceAppIsStable$ = concat(appIsStable$, everyHour$);

    everyHourOnceAppIsStable$.subscribe(async () => {
      try {
        await this.swUpdate.checkForUpdate();
      } catch (err) {
        this.loggingService.error('Failed to check for updates:', err);
      }
    });

    // Listen for version ready events (new version downloaded and ready)
    this.swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(async (evt) => {
        await this.promptUserToUpdate();
      });

    // Listen for unrecoverable state (service worker error) — auto-reload since there's nothing the user can do
    this.swUpdate.unrecoverable.subscribe((event) => {
      this.loggingService.error('Service Worker unrecoverable state:', event.reason);
      document.location.reload();
    });
  }

  /**
   * Show alert prompting user to update to the latest app version
   */
  private async promptUserToUpdate(): Promise<void> {
    const alert = await this.alertController.create({
      header: 'Update Available',
      message: 'A new version of e-rhema is available. Would you like to update now?',
      buttons: [
        {
          text: 'Later',
          role: 'cancel',
          handler: () => {}
        },
        {
          text: 'Update Now',
          handler: () => {
            // Activate the new version and reload the page
            this.swUpdate.activateUpdate().then(() => {
              window.location.reload();
            });
          }
        }
      ]
    });

    await alert.present();
  }

  /**
   * Show alert prompting user to reload the app (for unrecoverable errors)
   */
  private async promptUserToReload(header: string, message: string, buttonText: string): Promise<void> {
    const alert = await this.alertController.create({
      header,
      message,
      buttons: [
        {
          text: buttonText,
          handler: () => {
            window.location.reload();
          }
        }
      ]
    });

    await alert.present();
  }
}
